import React, { useMemo, useRef } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { InfoIcon } from 'icons/materialIcons';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip as ChartTooltip,
    Legend
} from 'chart.js';
import { Pie } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(ArcElement, ChartTooltip, Legend);

const LeadTypeBreakdownChartJS = ({ data = {} }) => {
    const chartRef = useRef();    // Normalize data to ensure we have only the 3 required lead types
    const normalizedData = useMemo(() => {
        return {
            leadCaptureForm: data.leadCaptureForm || 0,
            csv: data.csv || 0,
            manuallyAdded: data.manuallyAdded || 0
        };
    }, [data]);

    // Calculate total leads for percentage calculations
    const totalLeads = useMemo(() => {
        return Object.values(normalizedData).reduce((sum, val) => sum + val, 0);
    }, [normalizedData]);

    // Prepare data for display with calculated percentages - only 3 types with consistent colors
    const breakdownData = [
        {
            name: 'Lead capture form',
            value: normalizedData.leadCaptureForm,
            percentage: totalLeads > 0 ? ((normalizedData.leadCaptureForm / totalLeads) * 100).toFixed(1) : '0.0',
            color: '#EEA5F5'
        },
        {
            name: 'Import via CSV',
            value: normalizedData.csv,
            percentage: totalLeads > 0 ? ((normalizedData.csv / totalLeads) * 100).toFixed(1) : '0.0',
            color: '#FFB84D'
        },
        {
            name: 'Manually Added',
            value: normalizedData.manuallyAdded,
            percentage: totalLeads > 0 ? ((normalizedData.manuallyAdded / totalLeads) * 100).toFixed(1) : '0.0',
            color: '#FF6B6B'
        }
    ];

    // Filter out zero values for cleaner chart
    const filteredData = breakdownData.filter(item => item.value > 0);
    const hasData = filteredData.length > 0;

    // Chart.js data configuration
    const chartData = {
        labels: hasData ? filteredData.map(item => item.name) : ['No Data'],
        datasets: [
            {
                data: hasData ? filteredData.map(item => item.value) : [1],
                backgroundColor: hasData ? filteredData.map(item => item.color) : ['#E0E0E0'],
                borderColor: hasData ? filteredData.map(item => item.color) : ['#E0E0E0'],
                borderWidth: 2,
                hoverOffset: 6,
                cutout: '55%'
            }
        ]
    };

    // Chart.js options
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                titleColor: '#333333',
                bodyColor: '#666666',
                borderColor: '#E0E0E0',
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: true,
                titleFont: {
                    size: 12,
                    weight: 'bold'
                },
                bodyFont: {
                    size: 11
                },
                padding: 12,
                callbacks: {
                    label: function (context) {
                        if (!hasData) return 'No data available';
                        const item = filteredData[context.dataIndex];
                        return `${item.name}: ${item.value} leads (${item.percentage}%)`;
                    }
                }
            }
        },
        animation: {
            duration: 750,
            easing: 'easeInOutQuart'
        },
        onClick: (event, elements) => {
            if (elements.length > 0 && hasData) {
                const elementIndex = elements[0].index;
                const clickedItem = filteredData[elementIndex];
                console.log(`Clicked on ${clickedItem.name}: ${clickedItem.value} leads`);
            }
        }
    };

    return (
        <Box
            sx={{
                height: '350px',
                backgroundColor: '#ffffff',
                borderRadius: '18px',
                padding: '16px 20px',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    marginBottom: '20px'
                }}
            >
                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#333333' }}>
                    Lead Type Distribution
                </Typography>
                <Tooltip title="Distribution of leads by source type with interactive pie chart">
                    <InfoIcon sx={{ fontSize: 16, color: '#828282' }} />
                </Tooltip>
            </Box>

            {hasData ? (
                <>
                    {/* Chart */}
                    <Box
                        sx={{
                            height: '160px',
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginBottom: '20px'
                        }}
                    >
                        <Pie ref={chartRef} data={chartData} options={options} />
                    </Box>

                    {/* Legend */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {breakdownData.map((item, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '3px 0',
                                    opacity: item.value > 0 ? 1 : 0.4
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Box
                                        sx={{
                                            width: '10px',
                                            height: '10px',
                                            backgroundColor: item.color,
                                            borderRadius: '50%'
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '10px',
                                            fontWeight: 500,
                                            color: '#333333'
                                        }}
                                    >
                                        {item.name}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Typography sx={{ fontSize: '10px', color: '#666666' }}>
                                        {item.value}
                                    </Typography>
                                    <Typography sx={{ fontSize: '10px', color: '#888888', fontWeight: 500 }}>
                                        {item.percentage}%
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </>
            ) : (
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: '#888888'
                    }}
                >
                    <Typography sx={{ fontSize: '14px', marginBottom: '8px' }}>
                        No lead data available
                    </Typography>
                    <Typography sx={{ fontSize: '12px', textAlign: 'center' }}>
                        Lead distribution will appear here once you start generating leads
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default LeadTypeBreakdownChartJS;
